import Loading from "@/components/breadcrumbs/Loading";
import * as React from "react";
import {defineMessage, useIntl} from "react-intl";
import {FormattedPageTitle} from "@/components/pages";
import {DataSource, useDataSource} from "./dataSource";
import DomainQuotaDefaultsForm from "./form";
import {NumberField, FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {ListItemText, MenuItem, Select, Tooltip} from "@mui/material";
import {DefaultDevice} from "@/api";
import QuotaItemSelector from "@/components/QuotaItemSelector";
import {labelForCapabilitySet, labelForDeviceTypeSet} from "@/utils/translations";

const Content = ({domainId, dataSource}: {domainId: number; dataSource: DataSource}) => {
    const form = new DomainQuotaDefaultsForm(domainId, dataSource);
    const [enabled, setEnabled] = React.useState(true);
    const intl = useIntl();

    async function submit() {
        setEnabled(false);
        await form.submit();
        setEnabled(true);
    }

    // Since the FormContainer has a grid layout, this component is used to make margin work
    const RowDivider = () => (
        <>
            <div style={RowDividerStyle} />
            <div style={RowDividerStyle} />
        </>
    );

    /**
     * This component is used to display the default device field
     *
     * @param device The default device
     * @param onlyPackage If true, this field is disabled, since it can only be given by packages
     * @returns The component
     */
    const DeviceField = ({device, onlyPackage = false}: {device: DefaultDevice; onlyPackage?: boolean}) => (
        <NumberField
            value={device.value}
            update={(val) => form.updateDevice(device.name, val)}
            disabled={!enabled || onlyPackage}
            min={0}
            max={device.limit}
            style={{width: 100}}
        />
    );

    /**
     * This component is used to display the default device field that can only be given by packages
     *
     * @param device The default device
     * @returns The component
     */
    const DeviceFieldOnlyPackage = ({device}: {device: DefaultDevice}) => (
        <Tooltip
            title={intl.formatMessage({
                id: "domain_settings.only_from_package",
                defaultMessage: "Can not set default value for a device that can only be given from packages",
            })}
        >
            <div style={{width: "fit-content"}}>
                <DeviceField device={device} onlyPackage={true} />
            </div>
        </Tooltip>
    );

    /**
     * This function is used to tell the Select component how to render selected items
     *
     * @param caps The capabilities
     * @returns The formatted capabilities
     */
    function renderCapabilities(caps: string[]): string {
        return caps.length
            ? caps.map((cap) => intl.formatMessage(labelForCapabilitySet(cap))).join(", ")
            : intl.formatMessage({id: "none", defaultMessage: "None"});
    }

    return (
        <div>
            <div style={{marginBottom: "20px"}}>
                <FormattedPageTitle
                    desc={defineMessage({
                        id: "domain_settings.page_titles.quota_default",
                        defaultMessage: "Default quota values for domain {domain}",
                    })}
                    values={{domain: dataSource.domainName}}
                />
                <FormContainer style={{width: 600}}>
                    {dataSource.data.availableUserPackages.length > 0 && (
                        <>
                            <FormattedFormRow
                                label={defineMessage({id: "user_package", defaultMessage: "User package"})}
                            >
                                <QuotaItemSelector
                                    value={form.values.defaultUserPackage}
                                    updateValue={(pkg) => form.updateValues({defaultUserPackage: pkg})}
                                    disabled={!enabled}
                                    items={dataSource.data.availableUserPackages}
                                    initialValue={form.values.defaultUserPackage}
                                    itemNamePrefix="user_package_names."
                                />
                            </FormattedFormRow>
                            <RowDivider />
                        </>
                    )}
                    <FormattedFormRow
                        label={defineMessage({id: "extensionLimits.capabilities", defaultMessage: "Capabilities"})}
                    >
                        <Select
                            variant="standard"
                            value={form.values.defaultCapabilities ?? []}
                            onChange={(e) => form.updateValues({defaultCapabilities: e.target.value as string[]})}
                            renderValue={(selected) => renderCapabilities(selected)}
                            displayEmpty
                            multiple
                            disabled={!enabled}
                        >
                            {form.values.availableCapabilities.map((cap) => {
                                return (
                                    <MenuItem key={cap} value={cap}>
                                        <ListItemText primary={intl.formatMessage(labelForCapabilitySet(cap))} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormattedFormRow>
                    {form.values.defaultDevices.map((device: DefaultDevice, index: number) => {
                        return (
                            <React.Fragment key={device.name}>
                                <RowDivider />
                                <FormattedFormRow key={index} label={labelForDeviceTypeSet(device.name)}>
                                    {device.limit === 0 ? (
                                        <DeviceFieldOnlyPackage device={device} />
                                    ) : (
                                        <DeviceField device={device} />
                                    )}
                                </FormattedFormRow>
                            </React.Fragment>
                        );
                    })}
                    <FormattedFormRow>
                        <div style={{marginTop: "2rem"}}>
                            <FormActionButtons
                                mainAction={submit}
                                secondaryAction={() => form.reset()}
                                enabled={enabled && form.hasChanged}
                            />
                        </div>
                    </FormattedFormRow>
                </FormContainer>
            </div>
        </div>
    );
};

export const DomainQuotaDefaultsSettings = ({domainId}: {domainId: number}) => {
    const dataSource = useDataSource(domainId);

    if (dataSource.loading) return <Loading />;

    return <Content domainId={domainId} dataSource={dataSource} />;
};

const RowDividerStyle = {
    width: "100%",
    height: "0",
    margin: "5px 0",
} as React.CSSProperties;
